import {
  PUZZLE_MAX_AIM,
  PUZZLE_MIN_AIM,
  type PuzzleInput,
} from "../simulation";

type AimAction = "left" | "right" | "fire" | "hold";

/** 4 degrees per authoritative read = 120 degrees/second at 30 Hz. */
export const PUZZLE_KEYBOARD_AIM_STEP = 4;
interface AimGeometry {
  gameWidth: number;
  gameHeight: number;
  launcherX: number;
  launcherY: number;
}

const DEFAULT_AIM_GEOMETRY: AimGeometry = {
  gameWidth: 1280,
  gameHeight: 840,
  launcherX: 640,
  launcherY: 768
};

export function clampPuzzleAim(value: number): number {
  return Math.max(PUZZLE_MIN_AIM, Math.min(PUZZLE_MAX_AIM, value));
}

/**
 * Convert a pointer position into the canonical upward-facing launcher arc.
 * A drag below the launcher freezes the last valid aim instead of crossing
 * the downward atan2 seam and visually jumping to the opposite endpoint.
 */
export function puzzleAimFromPointerPosition(
  currentAim: number,
  pointerX: number,
  pointerY: number,
  launcherX: number,
  launcherY: number,
): number {
  if (
    !Number.isFinite(pointerX)
    || !Number.isFinite(pointerY)
    || !Number.isFinite(launcherX)
    || !Number.isFinite(launcherY)
  ) {
    return clampPuzzleAim(currentAim);
  }
  const upward = launcherY - pointerY;
  if (upward <= 0) return clampPuzzleAim(currentAim);
  const horizontal = pointerX - launcherX;
  return clampPuzzleAim(Math.atan2(horizontal, upward) * 180 / Math.PI);
}

/**
 * Browser input adapter for the puzzle simulation. Fire is latched until the
 * next simulation read so a quick touch release cannot disappear between ticks.
 */
export class PuzzleInputController {
  private enabled = false;
  private disposed = false;
  private currentAim = 0;
  private fireQueued = false;
  private holdQueued = false;
  private keyboardLeft = false;
  private keyboardRight = false;
  private activeAimPointer: number | null = null;
  private controlPointers = new Map<number, AimAction>();
  private controlCounts: Record<"left" | "right", number> = { left: 0, right: 0 };
  private aimSurface: HTMLElement | null = null;
  private controlsRoot: HTMLElement | null = null;
  private aimGeometry: AimGeometry = { ...DEFAULT_AIM_GEOMETRY };

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (!this.enabled) return;
    if (event.code === "ArrowLeft" || event.code === "KeyA") {
      event.preventDefault();
      this.keyboardLeft = true;
      return;
    }
    if (event.code === "ArrowRight" || event.code === "KeyD") {
      event.preventDefault();
      this.keyboardRight = true;
      return;
    }
    if (event.code === "Space" && !event.repeat) {
      event.preventDefault();
      this.fireQueued = true;
      return;
    }
    if ((event.code === "KeyC" || event.code === "ShiftLeft" || event.code === "ShiftRight") && !event.repeat) {
      event.preventDefault();
      this.holdQueued = true;
    }
  };

  private readonly handleKeyUp = (event: KeyboardEvent): void => {
    if (event.code === "ArrowLeft" || event.code === "KeyA") {
      event.preventDefault();
      this.keyboardLeft = false;
    } else if (event.code === "ArrowRight" || event.code === "KeyD") {
      event.preventDefault();
      this.keyboardRight = false;
    }
  };

  private readonly handleBlur = (): void => {
    this.keyboardLeft = false;
    this.keyboardRight = false;
    this.fireQueued = false;
    this.holdQueued = false;
    this.activeAimPointer = null;
    this.controlPointers.clear();
    this.controlCounts = { left: 0, right: 0 };
    this.syncPressedStates();
  };

  private readonly handleVisibilityChange = (): void => {
    if (document.hidden) this.handleBlur();
  };

  private readonly handleAimPointerDown = (event: PointerEvent): void => {
    if (!this.enabled || this.activeAimPointer !== null) return;
    event.preventDefault();
    this.activeAimPointer = event.pointerId;
    this.aimSurface?.setPointerCapture(event.pointerId);
    this.updateAimFromPointer(event);
  };

  private readonly handleAimPointerMove = (event: PointerEvent): void => {
    if (!this.enabled || this.activeAimPointer !== event.pointerId) return;
    event.preventDefault();
    this.updateAimFromPointer(event);
  };

  private readonly handleAimPointerUp = (event: PointerEvent): void => {
    if (!this.enabled || this.activeAimPointer !== event.pointerId) return;
    event.preventDefault();
    this.updateAimFromPointer(event);
    this.activeAimPointer = null;
    this.fireQueued = true;
  };

  private readonly handleAimPointerCancel = (event: PointerEvent): void => {
    if (this.activeAimPointer === event.pointerId) this.activeAimPointer = null;
  };

  private readonly handleControlPointerDown = (event: PointerEvent): void => {
    if (!this.enabled) return;
    const button = event.currentTarget as HTMLButtonElement;
    const action = button.dataset.puzzleAction as AimAction | undefined;
    if (!action || this.controlPointers.has(event.pointerId)) return;
    event.preventDefault();
    button.setPointerCapture(event.pointerId);
    this.controlPointers.set(event.pointerId, action);
    if (action === "fire") {
      this.fireQueued = true;
    } else if (action === "hold") {
      this.holdQueued = true;
    } else {
      this.controlCounts[action] += 1;
    }
    button.setAttribute("aria-pressed", "true");
  };

  private readonly handleControlPointerEnd = (event: PointerEvent): void => {
    const action = this.controlPointers.get(event.pointerId);
    if (!action) return;
    event.preventDefault();
    this.controlPointers.delete(event.pointerId);
    if (action === "left" || action === "right") {
      this.controlCounts[action] = Math.max(0, this.controlCounts[action] - 1);
    }
    const button = event.currentTarget as HTMLButtonElement;
    button.setAttribute("aria-pressed", "false");
  };

  private readonly handleControlClick = (event: MouseEvent): void => {
    // Pointer activation is handled on pointerdown. A detail of zero represents
    // keyboard activation, which still needs an accessible fire path.
    if (!this.enabled || event.detail !== 0) return;
    const action = (event.currentTarget as HTMLButtonElement).dataset.puzzleAction;
    if (action === "fire") this.fireQueued = true;
    else if (action === "hold") this.holdQueued = true;
  };

  private readonly preventContextMenu = (event: Event): void => event.preventDefault();

  constructor() {
    window.addEventListener("keydown", this.handleKeyDown, { passive: false });
    window.addEventListener("keyup", this.handleKeyUp, { passive: false });
    window.addEventListener("blur", this.handleBlur);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.fireQueued = false;
      this.holdQueued = false;
      this.handleBlur();
    }
  }

  attachAimSurface(surface: HTMLElement): void {
    this.detachAimSurface();
    this.aimSurface = surface;
    surface.addEventListener("pointerdown", this.handleAimPointerDown);
    surface.addEventListener("pointermove", this.handleAimPointerMove);
    surface.addEventListener("pointerup", this.handleAimPointerUp);
    surface.addEventListener("pointercancel", this.handleAimPointerCancel);
    surface.addEventListener("lostpointercapture", this.handleAimPointerCancel);
    surface.addEventListener("contextmenu", this.preventContextMenu);
  }

  attachControls(root: HTMLElement): void {
    this.detachControls();
    this.controlsRoot = root;
    root.querySelectorAll<HTMLButtonElement>("[data-puzzle-action]").forEach((button) => {
      button.addEventListener("pointerdown", this.handleControlPointerDown);
      button.addEventListener("pointerup", this.handleControlPointerEnd);
      button.addEventListener("pointercancel", this.handleControlPointerEnd);
      button.addEventListener("lostpointercapture", this.handleControlPointerEnd);
      button.addEventListener("click", this.handleControlClick);
      button.addEventListener("contextmenu", this.preventContextMenu);
    });
  }

  /** Current non-consuming aim for the renderer's dotted trajectory. */
  aim(): number {
    return this.currentAim;
  }

  setAim(aim: number): void {
    if (Number.isFinite(aim)) this.currentAim = clampPuzzleAim(aim);
  }

  setAimGeometry(geometry: AimGeometry): void {
    if (
      geometry.gameWidth > 0
      && geometry.gameHeight > 0
      && Number.isFinite(geometry.launcherX)
      && Number.isFinite(geometry.launcherY)
    ) {
      this.aimGeometry = { ...geometry };
    }
  }

  queueFire(): void {
    if (this.enabled) this.fireQueued = true;
  }

  /** Consumed exactly once per simulation tick by the authoritative driver. */
  read(): PuzzleInput {
    const left = this.keyboardLeft || this.controlCounts.left > 0;
    const right = this.keyboardRight || this.controlCounts.right > 0;
    if (left !== right) {
      this.currentAim = clampPuzzleAim(
        this.currentAim + (left ? -PUZZLE_KEYBOARD_AIM_STEP : PUZZLE_KEYBOARD_AIM_STEP),
      );
    }
    const input: PuzzleInput = {
      aim: this.currentAim,
      fire: this.fireQueued,
      hold: this.holdQueued
    };
    this.fireQueued = false;
    this.holdQueued = false;
    return input;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.setEnabled(false);
    this.detachAimSurface();
    this.detachControls();
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("keyup", this.handleKeyUp);
    window.removeEventListener("blur", this.handleBlur);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
  }

  private updateAimFromPointer(event: PointerEvent): void {
    const surface = this.aimSurface;
    if (!surface) return;
    const rect = surface.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    const { gameWidth, gameHeight, launcherX: logicalLauncherX, launcherY: logicalLauncherY } = this.aimGeometry;
    const gameAspect = gameWidth / gameHeight;
    const hostAspect = rect.width / rect.height;
    const canvasWidth = hostAspect > gameAspect ? rect.height * gameAspect : rect.width;
    const canvasHeight = hostAspect > gameAspect ? rect.height : rect.width / gameAspect;
    const canvasLeft = rect.left + (rect.width - canvasWidth) / 2;
    const canvasTop = rect.top + (rect.height - canvasHeight) / 2;
    const launcherX = canvasLeft + canvasWidth * (logicalLauncherX / gameWidth);
    const launcherY = canvasTop + canvasHeight * (logicalLauncherY / gameHeight);
    this.currentAim = puzzleAimFromPointerPosition(
      this.currentAim,
      event.clientX,
      event.clientY,
      launcherX,
      launcherY,
    );
  }

  private detachAimSurface(): void {
    const surface = this.aimSurface;
    if (!surface) return;
    surface.removeEventListener("pointerdown", this.handleAimPointerDown);
    surface.removeEventListener("pointermove", this.handleAimPointerMove);
    surface.removeEventListener("pointerup", this.handleAimPointerUp);
    surface.removeEventListener("pointercancel", this.handleAimPointerCancel);
    surface.removeEventListener("lostpointercapture", this.handleAimPointerCancel);
    surface.removeEventListener("contextmenu", this.preventContextMenu);
    this.aimSurface = null;
    this.activeAimPointer = null;
  }

  private detachControls(): void {
    this.controlsRoot?.querySelectorAll<HTMLButtonElement>("[data-puzzle-action]").forEach((button) => {
      button.removeEventListener("pointerdown", this.handleControlPointerDown);
      button.removeEventListener("pointerup", this.handleControlPointerEnd);
      button.removeEventListener("pointercancel", this.handleControlPointerEnd);
      button.removeEventListener("lostpointercapture", this.handleControlPointerEnd);
      button.removeEventListener("click", this.handleControlClick);
      button.removeEventListener("contextmenu", this.preventContextMenu);
    });
    this.controlsRoot = null;
    this.controlPointers.clear();
    this.controlCounts = { left: 0, right: 0 };
  }

  private syncPressedStates(): void {
    this.controlsRoot?.querySelectorAll<HTMLButtonElement>("[data-puzzle-action]").forEach((button) => {
      button.setAttribute("aria-pressed", "false");
    });
  }
}
